// components/entities/tables/brands/BrandsTable.tsx
'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '@/components/entities/EntityView';
import { BrandSortField, LeasingBrandDTO, LeasingBrandFilters } from '#types/types';
import { Table, TableColumnDef, TableFilterOption } from '../../../portal/Table';
import { BrandShortlistDetail } from '@/components/portal/BrandShortlistDetail';
import { Database, Diamond, ListChecks, MoveHorizontal, Ruler, ShieldCheck, Sparkles, Store, Tag, X } from '@/components/ui/lucide-memo';
import { BrandMark } from '@/components/ui/BrandMark';
import { BRAND_ORIGINS } from '#lib/brands/brandOrigin';
import { Network } from 'lucide-react';
import { BrandQualificationPanel, BrandStampHeadline } from '@/components/portal/BrandQualificationPanel';

const translations = {
  en: {
    relevance: 'Relevance',
    alphabetical: 'Alphabetical',
    locationsCount: 'Locations Count',
    standardSurface: 'Standard Surface',
    financialScore: 'Financial Score',
    revenueWeight: 'Revenue (Economic Weight)',
    growthDelta: 'Growth (Absolute Delta)',
    brandHeader: 'Brand',
    dataHeader: 'Countries',
    typologyHeader: 'Typology',
    positioningHeader: 'Positioning',
    surfaceHeader: 'Surface',
    networkHeader: 'Stores',
    networkHeaderTooltip: 'Stores found / network size the brand declares',
    spacingHeader: 'Spacing',
    openingsData: 'Openings data',
    dataComplete: 'Complete (official registry)',
    dataMissing: 'No openings data',
  },
  fr: {
    relevance: 'Pertinence',
    alphabetical: 'Alphabetique',
    locationsCount: 'Nombre de sites',
    standardSurface: 'Surface standard',
    financialScore: 'Score financier',
    revenueWeight: 'Revenu (poids eco)',
    growthDelta: 'Croissance (delta absolu)',
    brandHeader: 'Marque',
    dataHeader: 'Pays',
    typologyHeader: 'Typologie',
    positioningHeader: 'Positionnement',
    surfaceHeader: 'Surface',
    networkHeader: 'Magasins',
    networkHeaderTooltip: 'Magasins trouvés / taille de réseau déclarée',
    spacingHeader: 'Densite',
    openingsData: 'Données ouvertures',
    dataComplete: 'Complètes (registre officiel)',
    dataMissing: 'Sans données',
  },
};

interface BrandsTableProps {
  initialFilters?: Partial<LeasingBrandFilters>;
  /** Hide the toolbar search when the caller renders its own above the table. */
  hideSearch?: boolean;
  lockedFilters?: Partial<LeasingBrandFilters>;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  preview?: boolean;
  /** Admin Brands view: adds the Origin filter (how each brand entered the base). */
  showOrigin?: boolean;
  showQualification?: boolean;
  /** A control at the head of the toolbar's chip row (Data's coverage chip). */
  toolbarStart?: React.ReactNode;
}

export const BrandsTable = React.memo(({
  initialFilters,
  hideSearch,
  lockedFilters,
  assetLedgerTitle = 'All tenants',
  assetLedgerBreadcrumb = 'Inventory / Tenants',
  preview = false,
  showOrigin = false,
  showQualification = false,
  toolbarStart,
}: BrandsTableProps) => {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];

  const sortOptions = useMemo<{ label: string; field: BrandSortField; localAccessor?: (brand: LeasingBrandDTO) => unknown }[]>(() => [
    ...(showQualification ? [{ label: lang === 'fr' ? 'Priorité interne' : 'Internal priority', field: 'INTERNAL_PRIORITY' as const, localAccessor: (brand: LeasingBrandDTO) => brand.qualification?.stamp?.eligible ? brand.qualification.stamp.score : null }] : []),
    { label: t.relevance, field: 'RELEVANCE' },
    { label: t.alphabetical, field: 'NAME' },
    { label: t.locationsCount, field: 'LOCATION_COUNT' },
    { label: t.standardSurface, field: 'SURFACE' },
    { label: t.financialScore, field: 'FINANCIAL_SCORE' },
    { label: t.revenueWeight, field: 'REVENUE' },
    { label: t.growthDelta, field: 'GROWTH' },
  ], [t, showQualification, lang]);

  const tableFilters = useMemo<Partial<LeasingBrandFilters>>(() => ({
    ...(initialFilters || {}),
    ...(lockedFilters || {}),
    sortBy: lockedFilters?.sortBy ?? initialFilters?.sortBy ?? 'RELEVANCE',
    sortDir: lockedFilters?.sortDir ?? initialFilters?.sortDir ?? 'desc',
  }), [initialFilters, lockedFilters]);

  const columns = useMemo<TableColumnDef[]>(() => [
    { id: 'brand', icon: Store, minWidth: 240, header: t.brandHeader },
    ...(showQualification ? [
      { id: 'qualification', icon: ShieldCheck, minWidth: 120, align: 'center' as const, header: lang === 'fr' ? 'Qualification' : 'Qualification' },
      { id: 'internalPriority', icon: Diamond, minWidth: 90, align: 'center' as const, header: 'Score' },
      { id: 'aura', icon: Sparkles, minWidth: 100, align: 'center' as const, header: 'Aura' },
      { id: 'evidenceCoverage', icon: ListChecks, minWidth: 100, align: 'center' as const, header: lang === 'fr' ? 'Preuves' : 'Evidence' },
    ] : []),
    { id: 'data', icon: Database, minWidth: 120, header: t.dataHeader },
    { id: 'typology', icon: Tag, minWidth: 100, header: t.typologyHeader },
    { id: 'positioning', icon: Diamond, minWidth: 120, header: t.positioningHeader },
    { id: 'surface', icon: Ruler, minWidth: 100, header: t.surfaceHeader },
    {
      id: 'network',
      icon: Network,
      minWidth: 128,
      header: (
        <span title={t.networkHeaderTooltip} className="cursor-help">
          {t.networkHeader}
        </span>
      ),
    },
    { id: 'spacing', icon: MoveHorizontal, minWidth: 100, header: t.spacingHeader },
    { id: 'actions', minWidth: 80, header: '', align: 'right' },
  ], [t, showQualification, lang]);

  // Notion-style classification filter (data completeness). Geography is the folder browser.
  const filterOptions = useMemo<TableFilterOption<'Brand'>[]>(() => [
    ...(showQualification ? [{ key: 'qualificationStatus', label: lang === 'fr' ? 'Qualification' : 'Qualification', values: [
      { value: 'qualified', label: lang === 'fr' ? 'Qualifiée' : 'Qualified' },
      { value: 'hold', label: lang === 'fr' ? 'En attente' : 'On hold' },
      { value: 'unassessed', label: lang === 'fr' ? 'À évaluer' : 'Unassessed' },
    ] }] : []),
    {
      key: 'openingsData',
      label: t.openingsData,
      values: [
        { value: 'complete', label: t.dataComplete },
        { value: 'missing', label: t.dataMissing },
      ],
    },
    ...(showOrigin ? [{
      key: 'origin' as const,
      label: 'Origin',
      values: [
        ...Object.entries(BRAND_ORIGINS).map(([value, label]) => ({ value, label })),
        { value: 'UNKNOWN', label: 'Unknown' },
      ],
    }] : []),
  ], [t, showOrigin, showQualification, lang]);

  // A BRAND OPENS BESIDE THE LIST, NOT INSIDE IT (owner, 2026-09-30: 'it still looks very bad in
  // Data'). The detail is a document; unfolded as a band inside a fourteen-column grid it floated
  // in a wide grey strip. It now reads in its own full-height side panel, Notion's side peek or
  // ChatGPT's canvas, while the list stays where it was. Clicking another row switches brand.
  const [peekId, setPeekId] = useState<string | null>(null);
  const renderRow = useCallback(
    (brand: LeasingBrandDTO, _isExpanded: boolean, _toggle: () => void, currentColumns?: TableColumnDef[]) => (
      <EntityView
        type="Brand"
        entityId={brand.id}
        variant="row"
        onClick={() => setPeekId((open) => (open === brand.id ? null : brand.id))}
        columns={currentColumns}
        className={peekId === brand.id ? '!bg-[#1A1A1A]' : ''}
      />
    ),
    [peekId],
  );

  return (
    <>
    <Table
      hideSearch={hideSearch}
      beforeCreateContent={toolbarStart}
      entityType="Brand"
      preview={preview}
      initialFilters={tableFilters}
      lockedFilters={lockedFilters}
      columns={columns}
      filterOptions={filterOptions}
      inlineExpand
      assetLedgerTitle={assetLedgerTitle}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb}
      sortOptions={sortOptions}
      supportsStats
      statsEntityType="Brand"
      forceGlobalStats={true}
      renderRow={renderRow}
    />
    {peekId && typeof document !== 'undefined' && createPortal(
      <BrandPeek brandId={peekId} lang={lang} showQualification={showQualification} onClose={() => setPeekId(null)} />,
      document.body,
    )}
    </>
  );
});

BrandsTable.displayName = 'BrandsTable';

const PEEK_WIDTH_KEY = 'fater.brandPeekWidth';
const PEEK_DEFAULT = 680;
const PEEK_MIN = 420;

const BrandPeek = ({ brandId, lang, showQualification, onClose }: { brandId: string; lang: 'en' | 'fr'; showQualification: boolean; onClose: () => void }) => {
  const brand = useKernelStore((state) => state.inventory.Brand.registry[brandId]) as { name?: string; logoUrl?: string | null; category?: string | null; cnccActivity?: string | null } | undefined;
  // RESIZABLE, like Notion's side peek (owner, 2026-10-02): drag the left edge. The width is a fact
  // about this screen, so it is kept in this browser rather than in the account.
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? PEEK_DEFAULT : Number(window.localStorage.getItem(PEEK_WIDTH_KEY)) || PEEK_DEFAULT));
  const clampWidth = (value: number) => Math.round(Math.min(Math.max(value, PEEK_MIN), window.innerWidth * 0.9));
  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    let latest = width;
    const move = (e: PointerEvent) => { latest = clampWidth(window.innerWidth - e.clientX); setWidth(latest); };
    const end = () => { handle.removeEventListener('pointermove', move); handle.removeEventListener('pointerup', end); window.localStorage.setItem(PEEK_WIDTH_KEY, String(latest)); };
    handle.addEventListener('pointermove', move); handle.addEventListener('pointerup', end);
  };
  return (
    <aside
      role="dialog"
      aria-label={brand?.name ?? 'Brand'}
      // Escape closes the panel, not the Settings sheet underneath it.
      onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); onClose(); } }}
      style={{ width: `min(${width}px, 100vw)` }}
      className="brand-peek-in fixed inset-y-0 right-0 z-[120] flex flex-col border-l border-[#262626] bg-[#0D0D0D] shadow-[-24px_0_60px_rgba(0,0,0,0.55)]"
    >
      <div role="separator" aria-orientation="vertical" aria-label={lang === 'fr' ? 'Redimensionner le panneau' : 'Resize panel'} onPointerDown={startResize}
        onDoubleClick={() => { setWidth(PEEK_DEFAULT); window.localStorage.removeItem(PEEK_WIDTH_KEY); }}
        className="absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize transition-colors hover:bg-white/[0.12] active:bg-white/[0.2]" />
      <header className="flex shrink-0 items-center gap-3 px-6 pb-2 pt-5">
        {brand?.logoUrl ? <BrandMark logoUrl={brand.logoUrl} name={brand.name} size={40} radius={10} /> : null}
        <div className="min-w-0 flex-1">
          <h2 className="truncate font-semibold text-white" style={{ fontSize: 20, lineHeight: '26px' }}>{brand?.name ?? ''}</h2>
          {brand?.cnccActivity || brand?.category ? <p className="truncate text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>{brand.cnccActivity || brand.category}</p> : null}
        </div>
        <button type="button" autoFocus onClick={onClose} aria-label={lang === 'fr' ? 'Fermer' : 'Close'}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#AFAFAF] transition-colors hover:bg-white/[0.08] hover:text-white">
          <X size={18} strokeWidth={2} />
        </button>
      </header>
      <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-3">
        <BrandShortlistDetail
          brandId={brandId}
          lang={lang}
          headline={showQualification ? <BrandStampHeadline brandId={brandId} /> : undefined}
          scoring={showQualification ? <BrandQualificationPanel brandId={brandId} /> : undefined}
        />
      </div>
    </aside>
  );
};
